import moment from 'moment';

import { IEvent, IMonth } from '../../interfaces';
import { ActionType } from '../../classes/action-type';
import { checkIfUpdating, checkIfNewMonth } from '../actions/calendar.actions';

import { ICalendarStateAction } from '../interfaces/calendar-state.action';
import { ICalendarState, defaultCalendarState } from '../interfaces/calendar.state';

import { filterEvents } from '../../helpers/filter-events';
import { sortStrings } from '../../helpers/sort-strings';
import { IEventFilter } from '../../classes/calendar';
import { AddFilterToQuery } from '../../helpers/filter-by-query';
import { setCookie, dateCookieName } from '../../helpers/cookie-helper';

/**
 * Returns the new search state given a particular action. This where all of the application
 * logic resides.
 */
export default function calendarReducer(state: ICalendarState<IEvent> = defaultCalendarState, action: ICalendarStateAction): ICalendarState<IEvent> {
    switch (action.type) {

        /**
         * Checks various states to update global `updating` property.
         */
        case ActionType.CheckIfUpdating: {
            const updating = state.monthToLoad.length > 0

            return {
                ...state,
                updating
            };
        }

        /**
         * Checks if `currentDate` or `currentMonth` are outside the range of `loadedMonths`.
         * 
         * Checks if the `updating` state needs to change before returning the updated state.
         */
        case ActionType.CheckIfNewMonth: {
            let { loadedMonths, monthToLoad, currentMonth, venueFilter, availabilityFilter, genreFilter, typeFilter, filterFunction } = state;

            // check if the currentMonth is outside the range of loaded/loading months
            if (!loadedMonths.some(month => month === currentMonth.format('YYYY-MM-DD'))) {
                monthToLoad = currentMonth.format('YYYY-MM-DD');
            }

            // handle restricted ranges
            if (state.dateRangeFromQueryString) {
                // remove date-range restriction and display all loaded events
                const { genreFilter, venueFilter, availabilityFilter, typeFilter } = state;
                const rangeRestriction = { restricted: false };

                // update filtered events
                const [filteredEvents, filterHash] = filterEvents(state.events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter);

                return {
                    ...state,
                    dateRangeFromQueryString: false,
                    filteredEvents,
                    filterHash,
                    monthToLoad
                };
            } else {

                // if loading more dates, indicate filters should update
                //if (monthToLoad.length > 0) {
                //    adaFilter.loaded = false;
                //    discountFilter.loaded = false;
                //    genreFilter.loaded = false;
                //    typeFilter.loaded = false;
                //}

                return calendarReducer({
                    ...state,
                    monthToLoad,
                    venueFilter,
                    availabilityFilter,
                    genreFilter,
                    typeFilter,
                    filterFunction
                }, checkIfUpdating());
            }
        }

        /**
         * Update the MiniCalendar to display the next month.
         * 
         * Checks if a new month needs to be loaded before returning the updated state.
         */
        case ActionType.NextMonth: {
            const currentMonth = state.currentMonth.clone().add(1, 'month');
            const newDate = currentMonth.clone().startOf('month');

            return calendarReducer({
                ...state,
                currentDate: newDate,
                currentMonth
            }, checkIfNewMonth());
        }

        /**
         * Update the MiniCalendar to display the previous month.
         */
        case ActionType.PreviousMonth: {
            const previousMonth = state.currentMonth.clone().add(-1, 'month').startOf('month');
            const newDate = previousMonth.clone().startOf('month');

            if (previousMonth.isSameOrAfter(moment().startOf('month'))) {
                return calendarReducer({
                    ...state,
                    currentDate: newDate,
                    currentMonth: previousMonth
                }, checkIfNewMonth());
            } else {
                return state;
            }
        }

        /**
         * Update the displayed month in the sticky nav based on scroll position.
         */
        case ActionType.UpdateScrolledDate: {
            return {
                ...state,
                currentScrolledDate: action.value,
                currentDate: action.value
            };
        }

        case ActionType.SelectedDate: {
            return {
                ...state,
                currentDate: action.value,
                miniCalendarDateSelected: true,
                miniCalendarDate: action.value
            };
        }

        /**
         * Set the MiniCalendar's `currentDate` to a specific `moment`.
         * 
         * Checks if a new month needs to be loaded before returning the updated state.
         */
        case ActionType.SetDate: {
            const newDate = (action.value as moment.Moment).clone();
            const newMonth = newDate.clone().startOf('month');

            setCookie(dateCookieName, newDate.format(), 10);

            return calendarReducer({
                ...state,
                currentDate: newDate,
                currentMonth: newMonth
            }, checkIfNewMonth());
        }

        case ActionType.SetCurrentMonth: {
            const currentMonth = (action.value as moment.Moment).clone();
            const newDate = currentMonth.clone().startOf('month');

            setCookie(dateCookieName, newDate.format(), 10);

            return calendarReducer({
                ...state,
                currentDate: currentMonth,
                currentMonth: newDate
            }, checkIfNewMonth());
        }

        case ActionType.GoToMonth: {
            const setDate = action.value.clone() as moment.Moment;

            const newDate = setDate.clone().startOf('month');
            let newStart: moment.Moment;
            let newEnd: moment.Moment;

            let mustUpdate = false;

            if (setDate.isBefore(state.startDate)) {
                newStart = setDate.clone().startOf("month");
                mustUpdate = true;
            } else {
                newStart = state.startDate.clone();
            }

            if (setDate.isAfter(state.endDate)) {
                newEnd = setDate.clone().endOf("month");
                mustUpdate = true;
            } else {
                newEnd = state.endDate.clone();
            }

            return calendarReducer({
                ...state,
                updating: mustUpdate,
                currentDate: action.value.clone(),
                startDate: newStart,
                currentMonth: newDate,
                endDate: newEnd
            }, checkIfNewMonth());
        }


        case ActionType.UpdateEventFilter: {
            const updating = true;
            const addedFilterFunction = action.value.function;

            var eventFilter: IEventFilter[] = null;
            if (state.eventFilter.some(filter => { return filter.key === addedFilterFunction.key })) {
                eventFilter = state.eventFilter.filter(filter => !(filter.key === addedFilterFunction.key)).concat([addedFilterFunction]);
            } else {
                eventFilter = state.eventFilter.concat([addedFilterFunction])
            }
            const filterFunction = (event) => eventFilter.map(fil => { return fil.filter; }).every(f => f(event));

            if (action.value.shouldUpdateQuery) {
                AddFilterToQuery(eventFilter);
            }

            const { loadedMonths, monthToLoad, typeFilter, genreFilter, venueFilter, availabilityFilter } = state;
            const rangeRestriction = { restricted: false };
            const [filteredEvents, filterHash] = filterEvents(state.events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter);


            return { ...state, eventFilter, filterFunction, filteredEvents, updating, miniCalendarDateSelected: false, miniCalendarDate: null } as ICalendarState<IEvent>;
        }

        case ActionType.RemoveEventFilter: {
            const { loadedMonths, monthToLoad, typeFilter, genreFilter, venueFilter, availabilityFilter } = state;
            const rangeRestriction = { restricted: false };

            const eventFilter = state.eventFilter.filter(filter => filter.key !== action.value);
            const filterFunction = (event: IEvent) => eventFilter.map(filter => filter.filter).every(func => func(event));

            AddFilterToQuery(eventFilter);
            const [filteredEvents, filterHash] = filterEvents(state.events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter);


            return { ...state, eventFilter, filterFunction, filteredEvents, updating: true, miniCalendarDateSelected: false, miniCalendarDate: null } as ICalendarState<IEvent>;
        }

        case ActionType.RemoveAllEventFilters: {
            const { loadedMonths, monthToLoad, typeFilter, genreFilter, venueFilter, availabilityFilter } = state;
            const rangeRestriction = { restricted: false };

            let filterFunction = (event: IEvent) => true;
            AddFilterToQuery([]);
            const [filteredEvents, filterHash] = filterEvents(state.events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter);

            return {
                ...state, eventFilter: [], filterFunction: filterFunction,
                filteredEvents: state.events,
                updating: true,
                miniCalendarDateSelected: false,
                miniCalendarDate: null,
                currentScrolledDate: moment(),
                currentDate: moment(),
            } as ICalendarState<IEvent>;
        }

        /**
         * Open the MiniCalendar modal and store a reference to the button element which was clicked to open the modal.
         */
        case ActionType.OpenMiniCalDialog: {
            return {
                ...state,
                miniCalDialogOpen: true,
                openModalTrigger: action.value
            }
        }

        /**
         * Close the MiniCalendar modal and focus the reference to the button element which was clicked to open the modal.
         */
        case ActionType.CloseMiniCalDialog: {
            state.openModalTrigger.focus();
            return {
                ...state,
                miniCalDialogOpen: false,
                openModalTrigger: undefined
            }
        }

        /**
         * Open the Filters modal and store a reference to the button element which was clicked to open the modal.
         */
        case ActionType.OpenFiltersDialog: {
            return {
                ...state,
                filtersDialogOpen: true,
                openModalTrigger: action.value
            };
        }

        /**
         * Close the Filters modal and focus the reference to the button element which was clicked to open the modal.
         */
        case ActionType.CloseFiltersDialog: {
            state.openModalTrigger.focus();
            return {
                ...state,
                filtersDialogOpen: false,
                openModalTrigger: undefined
            };
        }


        //case ActionType.SetEventType: {
        //    var selectedEvent = action.value as string;
        //    return {
        //        ...state,
        //        selectedEventType: 
        //    };
        //}

        /**
         * Handle a click on the Load More button.
         * 
         * Either removes the restricted range (display all loaded events) or
         * triggers the app to load additional dates / filter options.
         */
        case ActionType.LoadMore: {
            const { loadedMonths, monthToLoad, typeFilter, genreFilter, venueFilter, availabilityFilter, filterFunction } = state;

            // remove a restricted range
            if (state.dateRangeFromQueryString) {

                // update filtered events
                const rangeRestriction = { restricted: false };
                const [filteredEvents, filterHash] = filterEvents(state.events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter);

                return {
                    ...state,
                    dateRangeFromQueryString: false,
                    filteredEvents,
                    filterHash
                };
            }

            // find the most recently loaded month
            //const allMonths = [...loadedMonths, ...monthToLoad].sort(sortStrings);
            //const lastLoaded = allMonths[loadedMonths.length - 1];

            // update currentMonth and monthsToLoad
            const currentMonth = moment(monthToLoad).clone().add(1, 'month');
            //monthsToLoad.push(currentMonth.format('YYYY-MM-DD'));

            // indicate that filters should load additional options
            //typeFilter.loaded = false;
            //genreFilter.loaded = false;
            //adaFilter.loaded = false;
            //discountFilter.loaded = false;

            return calendarReducer({
                ...state,
                currentMonth,
                monthToLoad: currentMonth.format('YYYY-MM-DD'),
                typeFilter,
                genreFilter,
                venueFilter,
                availabilityFilter,
                filterFunction
            }, checkIfUpdating());
        }
        case ActionType.UpdateGridEvents: {
            const events = action.value as Array<IMonth<any>>;

            return { ...state, updating: false, events: events } as ICalendarState<IEvent>;
        }

        case ActionType.UpdateNextMonthEvents: {
            const { venueFilter, availabilityFilter, genreFilter, typeFilter, filterFunction } = state;
            let { events } = state;
            let { monthToLoad } = state;
            // make sure we aren't adding duplicates
            const loadedMonth = action.value.loadedMonth as string;
            const month = action.value.month as IMonth<IEvent>;

            // save the events to the store
            events = [month];
            //loadedMonths = [...loadedMonth];
            monthToLoad = "";
            //monthsToLoad.splice(monthsToLoad.indexOf(loadedMonth), 1);



            // update filtered events
            const rangeRestriction = { restricted: state.dateRangeFromQueryString, startDate: state.startDate, endDate: state.endDate };
            const [filteredNextMonthEvents, filterHash] = monthToLoad.length === 0 ? filterEvents(events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter) : [state.filteredEvents, state.filterHash];
            let allEventTypes = [...new Set(events.flatMap(e => e.events.productions).flatMap(p => p.eventType).filter(e => e != ""))];
            let availableEventTypes = [...new Set(filteredNextMonthEvents.flatMap(e => e.events.productions).flatMap(p => p.eventType).filter(e => e != ""))];
            return calendarReducer({
                ...state,
                events: state.firstMonthEvents,
                loadedMonths: [...loadedMonth],
                monthToLoad,
                filteredEvents: state.filteredEvents,
                filteredNextMonthEvents,
                filterHash,
                allEventTypes: allEventTypes,
                availableEventTypes: availableEventTypes,
                miniCalendarDate: null, miniCalendarDateSelected: false,
            }, checkIfUpdating());
        }
    
        /**
         * Update the store with additional events from the API.
         */

        case ActionType.UpdateEvents: {
            const { venueFilter, availabilityFilter, genreFilter, typeFilter, filterFunction } = state;
            let { events } = state;
            let { monthToLoad } = state;
            // make sure we aren't adding duplicates
            const loadedMonth = action.value.loadedMonth as string;
            const month = action.value.month as IMonth<IEvent>;

            // save the events to the store
            events = [month];
            //loadedMonths = [...loadedMonth];
            monthToLoad = "";
            //monthsToLoad.splice(monthsToLoad.indexOf(loadedMonth), 1);



            // update filtered events
            const rangeRestriction = { restricted: state.dateRangeFromQueryString, startDate: state.startDate, endDate: state.endDate };
            const [filteredEvents, filterHash] = monthToLoad.length === 0 ? filterEvents(events, rangeRestriction, filterFunction, venueFilter, availabilityFilter, genreFilter, typeFilter) : [state.filteredEvents, state.filterHash];
            let allEventTypes = [...new Set(events.flatMap(e => e.events.productions).flatMap(p => p.eventType).filter(e => e != ""))];
            let availableEventTypes = [...new Set(filteredEvents.flatMap(e => e.events.productions).flatMap(p => p.eventType).filter(e => e != ""))];
            return calendarReducer({
                ...state,
                events,
                firstMonthEvents: events,
                loadedMonths: [...loadedMonth],
                monthToLoad,
                filteredEvents,
                filterHash,
                allEventTypes: allEventTypes,
                availableEventTypes: availableEventTypes,
                miniCalendarDate: null, miniCalendarDateSelected: false,
            }, checkIfUpdating());
        }
        default:
            return state;
    }
}
